/**
 * Redux Performance Middleware
 * 
 * Logs Redux actions with timing and state change information to help
 * identify performance bottlenecks in state management.
 */

import { Middleware, AnyAction } from '@reduxjs/toolkit';
import { perfLogger, PerformanceCategory } from '@common/utils/performance-logger';
import { noteReduxAction } from '@common/services/ui-stall-reporter';

// Actions to skip logging (too frequent or not relevant for performance analysis)
const SKIP_ACTIONS = new Set<string>([
  // Add any high-frequency actions that don't need logging here
  // Example: 'mouse/move', 'scroll/update', etc.
]);

// Track slow actions (> threshold ms)
const SLOW_ACTION_THRESHOLD = 100; // ms

// Track state size changes
let previousStateSize = 0;

/**
 * Calculate approximate object size in bytes
 */
function getObjectSize(obj: any): number {
  try {
    const str = JSON.stringify(obj);
    return new Blob([str]).size;
  } catch (e) {
    return 0;
  }
}

/**
 * Redux middleware that logs action timing and state changes
 */
export const performanceMiddleware: Middleware<{}, unknown, any> = (store) => (next) => (action: AnyAction) => {
  // Skip certain actions to reduce noise
  if (SKIP_ACTIONS.has(action.type)) {
    return next(action);
  }

  const startTime = performance.now();
  const stateBefore = store.getState();
  
  // Execute the action
  const result = next(action);
  
  const endTime = performance.now();
  const duration = endTime - startTime;
  const stateAfter = store.getState();
  
  // Calculate state size change
  const stateSizeAfter = getObjectSize(stateAfter);
  const stateSizeDelta = stateSizeAfter - previousStateSize;
  previousStateSize = stateSizeAfter;

  // Log action with timing
  if (duration > SLOW_ACTION_THRESHOLD) {
    // Slow action - log with warning
    console.warn(
      `[Redux] 🔴 SLOW ACTION (${duration.toFixed(2)}ms): ${action.type}`,
      { duration, stateSizeDelta, action }
    );
    perfLogger.mark(PerformanceCategory.REDUX, `SLOW: ${action.type}`, {
      duration: duration.toFixed(2),
      stateSizeMB: (stateSizeAfter / (1024 * 1024)).toFixed(2),
      stateSizeDelta: stateSizeDelta > 0 ? `+${(stateSizeDelta / 1024).toFixed(2)}KB` : `${(stateSizeDelta / 1024).toFixed(2)}KB`
    });
  } else if (duration > 10) {
    // Medium action - log for reference
    perfLogger.mark(PerformanceCategory.REDUX, action.type, {
      duration: duration.toFixed(2),
      stateSizeMB: (stateSizeAfter / (1024 * 1024)).toFixed(2)
    });
  }
  
  // Track large state changes
  if (Math.abs(stateSizeDelta) > 100000) { // > 100KB change
    console.log(
      `[Redux] 📊 LARGE STATE CHANGE: ${action.type}`,
      {
        delta: `${stateSizeDelta > 0 ? '+' : ''}${(stateSizeDelta / 1024).toFixed(2)}KB`,
        totalSize: `${(stateSizeAfter / (1024 * 1024)).toFixed(2)}MB`
      }
    );
  }

  return result;
};

/**
 * Lightweight Redux middleware for critical path (minimal overhead)
 * Only logs slow actions to avoid performance impact
 */
export const lightweightPerformanceMiddleware: Middleware<{}, unknown, any> = (store) => (next) => (action: AnyAction) => {
  const startTime = performance.now();
  const result = next(action);
  const duration = performance.now() - startTime;
  noteReduxAction(String(action.type));

  // Only log if action took significant time
  if (duration > SLOW_ACTION_THRESHOLD) {
    console.warn(`[Redux] 🔴 SLOW: ${action.type} (${duration.toFixed(2)}ms)`);
    perfLogger.mark(PerformanceCategory.REDUX, `SLOW: ${action.type}`, {
      duration: duration.toFixed(2)
    });
  }
  
  return result;
};

/**
 * Generate Redux performance report
 */
export function generateReduxPerformanceReport() {
  const reduxLogs = perfLogger.getLogsByCategory(PerformanceCategory.REDUX);
  
  console.group('[Redux] 📊 Performance Report');
  
  // Count by action type
  const actionCounts = new Map<string, number>();
  reduxLogs.forEach(log => {
    const count = actionCounts.get(log.operation) || 0;
    actionCounts.set(log.operation, count + 1);
  });
  
  console.log('Action Frequency:');
  const sorted = Array.from(actionCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);
  sorted.forEach(([action, count]) => {
    console.log(`  ${action}: ${count} times`);
  });
  
  // Slow actions
  const slowActions = reduxLogs.filter(log => 
    log.operation.includes('SLOW:')
  );
  
  if (slowActions.length > 0) {
    console.log('\n⚠️  Slow Actions (>' + SLOW_ACTION_THRESHOLD + 'ms):');
    slowActions.forEach(log => {
      console.log(`  ${log.operation}: ${log.duration?.toFixed(2)}ms`);
    });
  }
  
  console.groupEnd();
}

